/**
 * オーバーレイ (画像 / 動画 / ぼかし / モザイク / 図形 / テキスト / 進行バー / カウントダウンの矩形)
 * の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-F / §4.1-G / §4.1-V。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/overlay.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/overlay.py (AI 提案の正規化・部分操作・批評用の合成フレーム)
 *
 * ## 形
 *
 * `Overlay = {id, kind, rect, startSec?, endSec?, materialId?, isTemporary?, opacity?, fit?,
 *             strength?, label?,
 *             shape?, fill?, stroke?, strokeWidthPercent?, featherPercent?, radiusPercent?,
 *             rotationDeg?, text?, design?, align?, burnedTextId?,
 *             sourceStartSec?, track?, direction?, format?, keyframes?}`
 *
 * - `kind`: `image` (素材画像を貼る) / `video` (動画 PIP。§4.1-V) / `blur` (ぼかす) /
 *   `mosaic` (粗く潰す) / `shape` (図形を塗る。§4.1-V) / `text` (rect に収めた文字。§4.1-V) /
 *   `progress` (進行バー。§4.1-V) / `countdown` (残り秒。§4.1-V)
 * - `rect`: **出力の幅 / 高さに対する %** (0.1 刻み)。`w` / `h` は 1〜100、`x` / `y` は
 *   矩形が画面内に収まる範囲へクランプする (保存値 = 実効値)
 * - `startSec` / `endSec`: 表示する区間。**省略 = 全区間**。基準は置き場所で変わる:
 *   - `project.overlays` … 動画全体の絶対秒
 *   - `TimelineItem.overlays` … そのシーンの表示秒 (テロップの startSec / endSec と同じ基準)
 * - `materialId` + `isTemporary`: `kind: 'image'` / `'video'` のときだけ必須
 *   (素材参照。署名 URL `src` と素材尺 `srcDurationSec` は導出値で DB には保存しない)
 * - `opacity`: 画像の不透明度 (0.1〜1.0。既定 1.0 = キーごと省略)
 * - `fit`: 画像 / 動画の収め方 (`contain` 既定 / `cover`)。既定はキーごと省略
 * - `strength`: ぼかし半径 / モザイクのセル辺を**出力幅に対する %** で表す
 *   (blur 0〜5.0・既定 2.0・0 = ぼかさない / mosaic 1.0〜8.0・既定 3.0)。正規化で必ず埋める
 * - `label`: 短い説明 (AI / UI の表示用。人間向けの文言だけを入れる)
 * - `burnedTextId`: 焼き込み文字の隠蔽 (§4.1-U) が作った 2 枚の目印 (候補 id)。
 *   冪等な貼り直し・適用済み判定・解除・lint の抑止はこのキーで行う (`label` は使わない)
 *
 * ## 図形 (`kind: 'shape'`) とテキスト (`kind: 'text'`) — §4.1-V
 *
 * どちらも「rect の中に何を描くか」だけが違い、置き場所・座標系・区間の基準・トリム / 分割 /
 * 速度の規則・描画順は image / blur / mosaic と**同一**。
 *
 * - `shape`: `rect` / `rounded` / `ellipse` / `arrow` の 4 種。`fill` (既定 `#000000`)・
 *   `stroke` + `strokeWidthPercent` (出力幅に対する %・0〜2)・`featherPercent` (縁のぼかし・
 *   出力幅に対する %・0〜5)・`radiusPercent` (角丸。短辺に対する %・0〜50。`rounded` の既定 15)・
 *   `rotationDeg` (-180〜180・0.5 刻み)。`arrow` は rect の左中央から右中央へ向く矢印
 *   (軸の太さ = 高さの 35%・矢じりは高さいっぱい) で、向きは `rotationDeg` で回す
 * - `text`: `text` (1〜80 文字・改行可)・`design` (テロップ design の部分集合 = 文字の見た目だけ)・
 *   `align` (`left` / `center` / `right`・既定 center)・`rotationDeg`。**フォントサイズは持たず
 *   rect にフィットさせる** (overlayTextFontSizePx。文字幅係数 = 全角 1.0 / 半角 0.55)。
 *   縦書きは無い。絵文字はテキストとして受ける (合成フレーム (PIL) には出ない)
 * - `blur` / `mosaic` も `rotationDeg` で回せる (2026-10-29。斜めの看板などを隠すため)。
 *   回すのは「ぼかす範囲」(rect の中心を軸) で、背景そのものは回さない
 *
 * ## 動画 PIP / 進行バー / カウントダウン / キーフレーム — §4.1-V (§5 step 4.11)
 *
 * - `video`: `materialId` + `isTemporary` (動画素材)・`sourceStartSec` (素材の何秒から・既定 0)・
 *   `fit` / `opacity` / `radiusPercent`。**既定で無音**で**ループしない** (表示区間が素材尺
 *   (`srcDurationSec`) を超えた分は最終フレームで止まる。lint が medium で指摘する)。
 *   分割画面は「`video` を 2 枚、`fit: 'cover'` で並べる」で作る (専用の種類は持たない)。
 *   `audio` (`mute` 既定 / `mix` = 素材の音声を本線の音声に重ねて鳴らす) と `holdLastFrame`
 *   (既定 false。true なら素材の残り (`srcDurationSec − sourceStartSec`) が表示区間より短いとき
 *   末尾フレームで静止して区間の終わりまで保つ) は絵コンテのモーション混在
 *   (brand-cut-motion-graphics-plan.md §10.13) 用で、既定値はキーごと落とす
 * - `progress`: rect をトラックとして、**オーバーレイ自身の表示区間**を 0→1 で塗る。
 *   `fill` (既定 `#FFFFFF`) / `track` (下地・既定 `#FFFFFF33`) /
 *   `direction` (`ltr` 既定 / `rtl` / `ttb` / `btt`) / `radiusPercent`
 * - `countdown`: 残り秒 (**表示区間の終端 − t**) を text と同じ描画 (design / align) で描く。
 *   `format` は `seconds` (既定・`ceil(残り)` の整数) / `mmss` (`m:ss`)。
 *   3 秒以下で色を変えるような演出はしない (見た目は design に任せる)
 * - `keyframes`: `[{atSec, rect?, opacity?}]` を**オーバーレイ自身の表示開始からの秒**で持ち
 *   (置き場所に依らず同じ基準)、2 点以上で線形補間する。最初の点より前 / 最後の点より後は
 *   端の値で保持し、rect と opacity は**独立に**補間する (片方だけ持つ点も可)。
 *   トリム / 速度 / 分割では区間と同じ比で追従する (`clampOverlaysToDuration` /
 *   `rescaleOverlaysForSpeed` / `splitOverlays`)。UI では数値を触らない (AI 専用)
 *
 * ## 2 つの置き場所と描画順
 *
 * - `project.overlays` … **シーン層の外に 1 枚**だけ置く (motion / look の外側で、
 *   テロップ・トランジションの白レイヤーより**上**)。ロゴのウォーターマーク・バッジ用。
 *   シーン層の中に入れるとトランジションの重なり区間で 2 つのシーン層が二重に描き、
 *   入ってくる側の変形 (ズーム / スライド) にも引きずられるため、外に 1 枚に決めた
 * - `TimelineItem.overlays` … **motion 層の内側** (映像と一緒にズーム / パンする)。look はその上から掛かる。
 *   焼き込まれた不自然な文字を隠す救済と、商品画像の差し込み用
 *
 * ## モザイクの実装 (重要な割り切り)
 *
 * CSS だけでは真のモザイク (低解像度化 + 最近傍拡大) を作れない。映像層を複製して切り抜く方式は
 * 動画のデコードが二重になり、プレビュー (Chrome) とレンダラー (headless Chrome) で挙動も揃わない。
 * そこで **mosaic は「強いぼかし + 半透明のグレーパッチ」の近似**とし、4 実装 (FE プレビュー /
 * レンダラー / 批評用の合成フレーム / 書き出しの注記) すべてで同じ近似を使う (§4.1-G)。
 * 「隠す」目的は満たすが、モザイク特有の四角いブロックにはならない。
 */
import { z } from 'zod';
import { TimeAnchorFieldSchema } from './timeAnchorSchema';
import {
  clampSpansToDuration,
  isSpanActiveAt,
  rescaleSpansForSpeed,
  roundHalfUp as roundHalfUpShared,
  splitSpans,
} from './timedSpan';
import { formatDurationLabel, formatTimecode } from './timecode';

export const OVERLAY_KINDS = [
  'image',
  'video',
  'blur',
  'mosaic',
  'shape',
  'text',
  'progress',
  'countdown',
] as const;
export type OverlayKind = (typeof OVERLAY_KINDS)[number];

export const OVERLAY_FITS = ['contain', 'cover'] as const;
export type OverlayFit = (typeof OVERLAY_FITS)[number];

/** 図形の種類 (§4.1-V)。`arrow` は rect の左中央 → 右中央へ向く矢印。 */
export const OVERLAY_SHAPES = ['rect', 'rounded', 'ellipse', 'arrow'] as const;
export type OverlayShape = (typeof OVERLAY_SHAPES)[number];

/** テキストオーバーレイの横揃え (§4.1-V)。位置は rect が決めるので縦は常に中央。 */
export const OVERLAY_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type OverlayTextAlign = (typeof OVERLAY_TEXT_ALIGNS)[number];

/** rect の刻み (%)。保存値はこの刻みに丸める。 */
export const OVERLAY_RECT_STEP = 0.1;
/** rect の幅 / 高さ (%) の範囲。 */
export const OVERLAY_SIZE_MIN = 1;
export const OVERLAY_SIZE_MAX = 100;

/** 画像の不透明度。既定 (1.0) はキーごと落とす。 */
export const OVERLAY_OPACITY_MIN = 0.1;
export const OVERLAY_OPACITY_MAX = 1;
export const OVERLAY_OPACITY_DEFAULT = 1;

/** 画像の収め方の既定 (キーごと落とす)。 */
export const OVERLAY_FIT_DEFAULT: OverlayFit = 'contain';

/** ぼかしの強さ (出力幅に対する %)。 */
/** 0 はぼかさない (半径 0)。UI のスライダーの下限 (2026-10-29 に 0.5 → 0)。 */
export const OVERLAY_BLUR_STRENGTH_MIN = 0;
export const OVERLAY_BLUR_STRENGTH_MAX = 5;
export const OVERLAY_BLUR_STRENGTH_DEFAULT = 2;
/** 強さ (ぼかし / モザイク) の刻み。保存値は小数 1 桁 (resolveOverlayStrength が丸める)。 */
export const OVERLAY_STRENGTH_STEP = 0.1;

/** モザイクのセルの一辺 (出力幅に対する %)。 */
export const OVERLAY_MOSAIC_STRENGTH_MIN = 1;
export const OVERLAY_MOSAIC_STRENGTH_MAX = 8;
export const OVERLAY_MOSAIC_STRENGTH_DEFAULT = 3;

/**
 * モザイクの近似: セルの一辺 (px) に対するぼかし半径の比。
 * セルと同じだけぼかすと形が残りすぎるので、少し弱めて「潰れた」印象にする。
 */
export const OVERLAY_MOSAIC_BLUR_RATIO = 0.75;
/** モザイクの近似で重ねる半透明パッチ (平均色の代わりの中間グレー)。 */
export const OVERLAY_MOSAIC_PATCH_COLOR = 'rgba(128, 128, 128, 0.18)';
/** OVERLAY_MOSAIC_PATCH_COLOR の RGBA 成分 (合成フレーム (PIL) 用)。 */
export const OVERLAY_MOSAIC_PATCH_RGBA: readonly [number, number, number, number] = [128, 128, 128, 46];

/** label の最大文字数 (AI の暴走で長文が入るのを防ぐ)。 */
export const OVERLAY_LABEL_MAX_CHARS = 40;
/** burnedTextId の最大文字数 (外部から書かれた長い文字列を保存形に残さない)。 */
export const OVERLAY_BURNED_TEXT_ID_MAX_CHARS = 40;

/**
 * 1 か所 (動画全体 / 1 シーン) に置けるオーバーレイの上限。
 * **backend/services/video_edit/validation.py の `MAX_OVERLAYS_PER_SCOPE` と同値**でなければ
 * ならない (超えると保存が 400 で弾かれる)。`backend/tests/test_video_edit_overlay.py` の
 * `OverlayMaxPerScopeSyncTests` がこのファイルを読んで一致を検証している。
 */
export const OVERLAY_MAX_PER_SCOPE = 12;

/** 1 つのオーバーレイが覆ってよい面積の上限 (%)。超えたら lint が指摘する (却下はしない)。 */
export const OVERLAY_AREA_WARN_PERCENT = 40;
/** 1 本の動画に置いてよい image オーバーレイの種類数 (超えたら lint が指摘する)。 */
export const OVERLAY_IMAGE_KIND_WARN_COUNT = 2;

// ── 図形 (kind: 'shape') の値空間 (§4.1-V) ──────────────────────────────

/** 塗りの既定色 (指定が無ければ黒で塗る)。 */
export const OVERLAY_SHAPE_FILL_DEFAULT = '#000000';
export const OVERLAY_SHAPE_DEFAULT: OverlayShape = 'rect';

/** 枠線の太さ (出力幅に対する %)。0 = 枠線なし (既定)。 */
export const OVERLAY_STROKE_WIDTH_MIN = 0;
export const OVERLAY_STROKE_WIDTH_MAX = 2;
export const OVERLAY_STROKE_WIDTH_DEFAULT = 0;

/** 縁のぼかし (出力幅に対する %)。0 = ぼかさない (既定)。 */
export const OVERLAY_FEATHER_MIN = 0;
export const OVERLAY_FEATHER_MAX = 5;
export const OVERLAY_FEATHER_DEFAULT = 0;

/**
 * 角丸の半径 (**短辺**に対する %)。0〜50 (50 = 短辺の半分 = 完全な丸)。
 * 今回は `kind: 'shape'` でだけ効く (image / video / progress への展開は §4.11)。
 */
export const OVERLAY_RADIUS_MIN = 0;
export const OVERLAY_RADIUS_MAX = 50;
export const OVERLAY_RADIUS_DEFAULT = 0;
/** `shape: 'rounded'` で radiusPercent を省略したときの既定 (§4.1-V)。 */
export const OVERLAY_ROUNDED_RADIUS_DEFAULT = 15;

/** 図形 / テキスト / ぼかし / モザイクの回転 (度・正 = 時計回り)。0.5 刻み。 */
export const OVERLAY_ROTATION_MIN_DEG = -180;
export const OVERLAY_ROTATION_MAX_DEG = 180;
export const OVERLAY_ROTATION_STEP_DEG = 0.5;

/** `arrow` の軸の太さ (rect の高さに対する比)。 */
export const OVERLAY_ARROW_SHAFT_RATIO = 0.35;
/** `arrow` の矢じりの長さ (rect の高さに対する比。rect の幅を超えない)。 */
export const OVERLAY_ARROW_HEAD_RATIO = 0.6;

// ── テキスト (kind: 'text') の値空間 (§4.1-V) ───────────────────────────

/** 文字数の上限 (改行を含む)。 */
export const OVERLAY_TEXT_MAX_CHARS = 80;
export const OVERLAY_TEXT_ALIGN_DEFAULT: OverlayTextAlign = 'center';
/** 既定の文字色 / ウェイト (design を省略したとき)。 */
export const OVERLAY_TEXT_DEFAULT_COLOR = '#FFFFFF';
export const OVERLAY_TEXT_DEFAULT_FONT_WEIGHT = 700;
/** 既定の縁取り (焼き込み文字の差し替えは「縁取り無し」が既定。§4.1-U)。 */
export const OVERLAY_TEXT_DEFAULT_OUTLINE_WIDTH_PX = 0;
/** rect にフィットさせるときの余白比 (高さ方向 / 幅方向)。 */
export const OVERLAY_TEXT_HEIGHT_RATIO = 0.82;
export const OVERLAY_TEXT_WIDTH_RATIO = 0.96;
/** 行送り (フォントサイズに対する倍率)。フィットの式と合わせて rect に収まる。 */
export const OVERLAY_TEXT_LINE_HEIGHT = 1.2;
/** 文字幅係数 (全角 1.0 / 半角 0.55)。3 実装で同値。 */
export const OVERLAY_TEXT_FULL_WIDTH_UNITS = 1;
export const OVERLAY_TEXT_HALF_WIDTH_UNITS = 0.55;

// ── 動画 PIP (kind: 'video') の値空間 (§4.1-V) ─────────────────────────

/** 素材の何秒から流すか。負値・非数は 0 (先頭) に倒す。 */
export const OVERLAY_VIDEO_SOURCE_START_DEFAULT = 0;

/**
 * 音の扱い (§10.13 絵コンテのモーション混在)。`mute` = 無音 (既定・従来どおり)、
 * `mix` = 素材の音声を本線の音声に重ねて鳴らす (効果音ミックス済みのモーション MP4 を
 * 利用者がオプトインしたとき)。未知の値は既定に倒す。
 */
export const OVERLAY_VIDEO_AUDIO_MODES = ['mute', 'mix'] as const;
export type OverlayVideoAudio = (typeof OVERLAY_VIDEO_AUDIO_MODES)[number];
export const OVERLAY_VIDEO_AUDIO_DEFAULT: OverlayVideoAudio = 'mute';
export const OVERLAY_VIDEO_AUDIO_LABELS: Record<OverlayVideoAudio, string> = {
  mute: '無音',
  mix: '音を重ねる',
};
/**
 * 素材が表示区間より短いとき、末尾フレームで静止して区間の終わりまで保つか
 * (既定 false = 従来どおり。true は `srcDurationSec` が分かるときだけ効く)。
 */
export const OVERLAY_VIDEO_HOLD_LAST_FRAME_DEFAULT = false;

/** 絵コンテのモーション上書き (§10.13) の載せ方。storyboardSpans.resolveMotionOverlay の fit と同じ値空間。 */
export const OVERLAY_MOTION_FITS = ['exact', 'trim', 'freeze', 'stale'] as const;
export type OverlayMotionFit = (typeof OVERLAY_MOTION_FITS)[number];

// ── 進行バー (kind: 'progress') の値空間 (§4.1-V) ───────────────────────

/** 塗りの進む向き。`ltr` = 左→右 (既定)。 */
export const OVERLAY_PROGRESS_DIRECTIONS = ['ltr', 'rtl', 'ttb', 'btt'] as const;
export type OverlayProgressDirection = (typeof OVERLAY_PROGRESS_DIRECTIONS)[number];
export const OVERLAY_PROGRESS_DIRECTION_DEFAULT: OverlayProgressDirection = 'ltr';
/** 進んだ部分の色 (既定)。 */
export const OVERLAY_PROGRESS_FILL_DEFAULT = '#FFFFFF';
/** 下地 (トラック) の色 (既定)。 */
export const OVERLAY_PROGRESS_TRACK_DEFAULT = '#FFFFFF33';

export const OVERLAY_PROGRESS_DIRECTION_LABELS: Record<OverlayProgressDirection, string> = {
  ltr: '左→右',
  rtl: '右→左',
  ttb: '上→下',
  btt: '下→上',
};

// ── カウントダウン (kind: 'countdown') の値空間 (§4.1-V) ────────────────

/** `seconds` = `ceil(残り)` の整数 (既定) / `mmss` = `m:ss`。 */
export const OVERLAY_COUNTDOWN_FORMATS = ['seconds', 'mmss'] as const;
export type OverlayCountdownFormat = (typeof OVERLAY_COUNTDOWN_FORMATS)[number];
export const OVERLAY_COUNTDOWN_FORMAT_DEFAULT: OverlayCountdownFormat = 'seconds';

export const OVERLAY_COUNTDOWN_FORMAT_LABELS: Record<OverlayCountdownFormat, string> = {
  seconds: '秒数',
  mmss: '分:秒',
};

// ── キーフレーム (全種類共通) の値空間 (§4.1-V) ─────────────────────────

/**
 * 1 つのオーバーレイが持てるキーフレームの上限。AI が長い配列を書いても保存形が膨らまないよう、
 * 正規化で先頭から この件数だけを残す (2 点で直線・4〜6 点あれば十分な表現力がある)。
 */
export const OVERLAY_MAX_KEYFRAMES = 24;
/** キーフレームの秒 / 補間結果の丸め桁 (3 実装で同値にするため明示する)。 */
const OVERLAY_KEYFRAME_SEC_DIGITS = 2;
const OVERLAY_KEYFRAME_RECT_DIGITS = 2;
const OVERLAY_KEYFRAME_OPACITY_DIGITS = 3;

export const OverlayRectSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});
export type OverlayRect = z.infer<typeof OverlayRectSchema>;

/**
 * テキストオーバーレイの文字の見た目 (§4.1-V)。**テロップの `CaptionDesign` の部分集合**で、
 * 位置・背景・アニメーション・回転・縦書き・単語ハイライトは受けない (位置は rect が決め、
 * 回転は overlay 側の `rotationDeg`)。`fontSizePx` も持たない (rect にフィットさせる)。
 *
 * 計画 (§4.1-V) は `letterSpacing` も挙げていたが、**`CaptionDesign` に letterSpacing は無い**
 * (テロップ側も字間を持たない) ので採用していない。字間を足すならテロップ側と同時に入れる。
 *
 * 描画は 3 実装とも「テロップと同じ縁取り / 影の実装」を再利用する
 * (FE / レンダラーの remotion/captionOutline.ts + resolveCaptionShadow、
 * backend の contact_sheet._resolve_caption_shadow)。
 */
export const OverlayTextDesignSchema = z.object({
  fontFamily: z.string().optional(),
  fontWeight: z.union([z.enum(['normal', 'bold']), z.number()]).optional(),
  color: z.string().optional(),
  outlineWidthPx: z.number().optional(),
  outlineColor: z.string().optional(),
  outlineJoin: z.enum(['round', 'miter']).optional(),
  outline2WidthPx: z.number().optional(),
  outline2Color: z.string().optional(),
  shadowColor: z.string().optional(),
  shadowOpacity: z.number().optional(),
  shadowSizePx: z.number().optional(),
  shadowBlurPx: z.number().optional(),
  shadowOffsetXPx: z.number().optional(),
  shadowOffsetYPx: z.number().optional(),
});
export type OverlayTextDesign = z.infer<typeof OverlayTextDesignSchema>;

/** `design` で受け付けるキー (正規化はこの一覧だけを写す)。 */
export const OVERLAY_TEXT_DESIGN_KEYS = [
  'fontFamily',
  'fontWeight',
  'color',
  'outlineWidthPx',
  'outlineColor',
  'outlineJoin',
  'outline2WidthPx',
  'outline2Color',
  'shadowColor',
  'shadowOpacity',
  'shadowSizePx',
  'shadowBlurPx',
  'shadowOffsetXPx',
  'shadowOffsetYPx',
] as const;

/**
 * キーフレーム 1 点 (§4.1-V)。`atSec` は**オーバーレイ自身の表示開始からの秒**で、
 * `rect` / `opacity` はどちらか片方だけでもよい (欠けた成分はその時点の基準値を使う)。
 */
export const OverlayKeyframeSchema = z.object({
  atSec: z.number(),
  rect: OverlayRectSchema.optional(),
  opacity: z.number().optional(),
});
export type OverlayKeyframe = z.infer<typeof OverlayKeyframeSchema>;

export const OverlaySchema = z.object({
  id: z.string().min(1),
  kind: z.enum(OVERLAY_KINDS),
  rect: OverlayRectSchema,
  /** 表示区間。省略 = 全区間 (置き場所によって「動画全体」か「そのシーン」か が変わる)。 */
  startSec: z.number().nonnegative().optional(),
  endSec: z.number().nonnegative().optional(),
  /**
   * 置き場所の正 (lineage 計画 §4.1。**動画全体のオーバーレイ (project.overlays) だけ**。シーンのオーバーレイは
   * シーンの表示秒基準なので持たない)。startSec が数値のときだけ anchor、endSec が数値のときだけ anchorEnd。
   * 両方省略 (全編) は錨なし。startSec / endSec はここから導出したキャッシュ (schema/timeAnchor.ts)。
   */
  anchor: TimeAnchorFieldSchema,
  anchorEnd: TimeAnchorFieldSchema,
  /** kind: 'image' のときだけ必須。素材参照 (署名 URL は導出値なので保存しない)。 */
  materialId: z.string().min(1).optional(),
  isTemporary: z.boolean().optional(),
  /** 導出値: ロード時に充填する署名付き URL。DB には保存しない (stripResolvedUrls / _STRIP_KEYS)。 */
  src: z.string().optional(),
  /**
   * 導出値: 署名 URL 解決時に `materials.duration_seconds` から充填する素材の実尺 (秒)。
   * DB には保存しない (stripResolvedUrls / _STRIP_KEYS)。`kind: 'video'` の lint にだけ使う。
   */
  srcDurationSec: z.number().optional(),
  /** 動画 PIP (§4.1-V): 素材の何秒から流すか (既定 0)。 */
  sourceStartSec: z.number().optional(),
  /** 動画 PIP (§10.13): 音の扱い (`mute` 既定 = 無音 / `mix` = 素材の音声を本線に重ねる)。 */
  audio: z.enum(OVERLAY_VIDEO_AUDIO_MODES).optional(),
  /**
   * 動画 PIP (§10.13): 素材が表示区間より短いとき末尾フレームで静止して区間の終わりまで保つ
   * (既定 false)。
   */
  holdLastFrame: z.boolean().optional(),
  /**
   * 絵コンテのモーション混在 (§10.13 契約 D) で mapper が載せた上書きの目印。どのシーンの上書きか
   * (sceneId) と載せ方 (fit: exact / trim / freeze / stale) を持ち、シーンカードの鮮度表示・
   * 再同期がこのキーで上書きを束ねる。描画には使わない (レンダラーは読まない) が、zod の
   * z.object は未知キーを落とすので 3 実装とも同じ形で宣言して素通しさせる。
   */
  motionOverlay: z.object({
    sceneId: z.string().min(1),
    fit: z.enum(OVERLAY_MOTION_FITS),
  }).optional(),
  /**
   * 範囲をスキーマで縛らない (DuckingSchema と同じ方針): 外部から書き換えられた JSON で
   * プロジェクト全体が開けなくなるのを避け、クランプは normalizeOverlay が行う。
   */
  opacity: z.number().optional(),
  fit: z.enum(OVERLAY_FITS).optional(),
  strength: z.number().optional(),
  label: z.string().optional(),
  // ── kind: 'shape' (§4.1-V) ──
  shape: z.enum(OVERLAY_SHAPES).optional(),
  fill: z.string().optional(),
  stroke: z.string().optional(),
  strokeWidthPercent: z.number().optional(),
  featherPercent: z.number().optional(),
  /** 角丸 (短辺に対する %)。今回は shape でだけ効く。 */
  radiusPercent: z.number().optional(),
  /** 図形 / テキスト / ぼかし / モザイクの回転 (度・正 = 時計回り)。 */
  rotationDeg: z.number().optional(),
  // ── kind: 'progress' (§4.1-V) ──
  /** 下地 (トラック) の色。`fill` は図形と共用 (進んだ部分の色)。 */
  track: z.string().optional(),
  /** 塗りの進む向き (`ltr` 既定)。 */
  direction: z.enum(OVERLAY_PROGRESS_DIRECTIONS).optional(),
  // ── kind: 'countdown' (§4.1-V) ──
  /** 残り秒の書き方 (`seconds` 既定 / `mmss`)。 */
  format: z.enum(OVERLAY_COUNTDOWN_FORMATS).optional(),
  // ── 全種類共通 (§4.1-V) ──
  /**
   * 位置 / 不透明度のキーフレーム (オーバーレイ自身の表示開始からの秒)。
   * 2 点以上で線形補間し、端の外は端の値で保持する。UI は数値を触らない (AI 専用)。
   */
  keyframes: z.array(OverlayKeyframeSchema).optional(),
  // ── kind: 'text' (§4.1-V) ──
  text: z.string().optional(),
  design: OverlayTextDesignSchema.optional(),
  align: z.enum(OVERLAY_TEXT_ALIGNS).optional(),
  /**
   * 焼き込み文字の隠蔽 / 差し替え (§4.1-U) が作った 2 枚の目印 (候補 id)。
   * 冪等な貼り直し・適用済み判定・解除・lint の抑止はすべて**このキー**で行う
   * (`label` は人間向けの説明に使うので、機械の目印には使わない)。
   */
  burnedTextId: z.string().optional(),
});
export type Overlay = z.infer<typeof OverlaySchema>;

export const OVERLAY_KIND_LABELS: Record<OverlayKind, string> = {
  image: '画像',
  video: '動画',
  blur: 'ぼかし',
  mosaic: 'モザイク',
  shape: '図形',
  text: 'テキスト',
  progress: '進行バー',
  countdown: 'カウントダウン',
};

export const OVERLAY_SHAPE_LABELS: Record<OverlayShape, string> = {
  rect: '四角',
  rounded: '角丸',
  ellipse: '楕円',
  arrow: '矢印',
};

export const OVERLAY_FIT_LABELS: Record<OverlayFit, string> = {
  contain: '全体を収める',
  cover: '枠いっぱいに',
};

/** UI のプリセット位置 (rect の x / y だけを決める)。 */
export const OVERLAY_POSITION_PRESETS = [
  'top_left',
  'top_right',
  'bottom_left',
  'bottom_right',
  'center',
  'full',
] as const;
export type OverlayPositionPreset = (typeof OVERLAY_POSITION_PRESETS)[number];

export const OVERLAY_POSITION_PRESET_LABELS: Record<OverlayPositionPreset, string> = {
  top_left: '左上',
  top_right: '右上',
  bottom_left: '左下',
  bottom_right: '右下',
  center: '中央',
  full: '全面',
};

/** 「全面」の矩形 (画面全体)。幅 / 高さを無視して画面いっぱいに敷く。 */
export const OVERLAY_FULL_RECT: OverlayRect = { x: 0, y: 0, w: OVERLAY_SIZE_MAX, h: OVERLAY_SIZE_MAX };

/** 矩形が画面全体 (= 「全面」) か。 */
export function isOverlayFullRect(rect: OverlayRect): boolean {
  return (
    rect.x === OVERLAY_FULL_RECT.x &&
    rect.y === OVERLAY_FULL_RECT.y &&
    rect.w === OVERLAY_FULL_RECT.w &&
    rect.h === OVERLAY_FULL_RECT.h
  );
}

/** プリセット位置の余白 (%)。ロゴの定位置 (隅から 3%) に合わせる。 */
export const OVERLAY_PRESET_MARGIN_PERCENT = 3;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * half-up の丸め。JS の Math.round は負値で half-down になる (-0.5 → -0) ので、
 * Python の実装と揃えるために schema/timedSpan.ts のものを使う (区間の丸めと同じ関数)。
 */
const roundHalfUp = roundHalfUpShared;

/** rect を保存形 (0.1 刻み・画面内) に収める。壊れた入力は undefined。 */
export function normalizeOverlayRect(value: unknown): OverlayRect | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const num = (key: string): number | undefined => {
    const v = raw[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  };
  const w0 = num('w');
  const h0 = num('h');
  const x0 = num('x');
  const y0 = num('y');
  if (w0 === undefined || h0 === undefined || x0 === undefined || y0 === undefined) return undefined;
  const w = roundHalfUp(clamp(w0, OVERLAY_SIZE_MIN, OVERLAY_SIZE_MAX), 1);
  const h = roundHalfUp(clamp(h0, OVERLAY_SIZE_MIN, OVERLAY_SIZE_MAX), 1);
  // 画面内へ押し戻す (拡大の余白と同じ考え方。保存値 = 実効値)
  const x = roundHalfUp(clamp(x0, 0, OVERLAY_SIZE_MAX - w), 1);
  const y = roundHalfUp(clamp(y0, 0, OVERLAY_SIZE_MAX - h), 1);
  return { x, y, w, h };
}

/** その kind で有効な strength の範囲と既定値。 */
export function overlayStrengthRange(kind: OverlayKind): { min: number; max: number; def: number } {
  if (kind === 'mosaic') {
    return {
      min: OVERLAY_MOSAIC_STRENGTH_MIN,
      max: OVERLAY_MOSAIC_STRENGTH_MAX,
      def: OVERLAY_MOSAIC_STRENGTH_DEFAULT,
    };
  }
  return {
    min: OVERLAY_BLUR_STRENGTH_MIN,
    max: OVERLAY_BLUR_STRENGTH_MAX,
    def: OVERLAY_BLUR_STRENGTH_DEFAULT,
  };
}

/** 実効の strength (保存形は必ず持つが、旧データ・壊れた値のための保険)。 */
export function resolveOverlayStrength(overlay: Pick<Overlay, 'kind' | 'strength'>): number {
  const { min, max, def } = overlayStrengthRange(overlay.kind);
  if (typeof overlay.strength !== 'number' || !Number.isFinite(overlay.strength)) return def;
  return roundHalfUp(clamp(overlay.strength, min, max), 1);
}

/** 実効の不透明度 (image 以外では意味を持たない)。 */
export function resolveOverlayOpacity(overlay: Pick<Overlay, 'opacity'>): number {
  if (typeof overlay.opacity !== 'number' || !Number.isFinite(overlay.opacity)) {
    return OVERLAY_OPACITY_DEFAULT;
  }
  return roundHalfUp(clamp(overlay.opacity, OVERLAY_OPACITY_MIN, OVERLAY_OPACITY_MAX), 2);
}

/** 実効の収め方。 */
export function resolveOverlayFit(overlay: Pick<Overlay, 'fit'>): OverlayFit {
  return overlay.fit === 'cover' ? 'cover' : OVERLAY_FIT_DEFAULT;
}

// ── 図形 / テキストの解決 (§4.1-V。3 実装で同値) ─────────────────────────

/** `#rgb` / `#rgba` / `#rrggbb` / `#rrggbbaa` だけを許す (CSS 名・rgb() は受けない)。 */
export function normalizeOverlayColor(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(text)) return undefined;
  // 大文字に揃える (3 実装で保存形を一致させるため)
  return `#${text.slice(1).toUpperCase()}`;
}

/** 実効の図形 (未指定・不正値は rect)。 */
export function resolveOverlayShape(overlay: Pick<Overlay, 'shape'>): OverlayShape {
  return overlay.shape && (OVERLAY_SHAPES as readonly string[]).includes(overlay.shape)
    ? overlay.shape
    : OVERLAY_SHAPE_DEFAULT;
}

/** 実効の塗り色。 */
export function resolveOverlayFill(overlay: Pick<Overlay, 'fill'>): string {
  return normalizeOverlayColor(overlay.fill) ?? OVERLAY_SHAPE_FILL_DEFAULT;
}

/** 実効の枠線の太さ (出力幅に対する %)。色が無ければ 0 (枠線なし)。 */
export function resolveOverlayStrokeWidthPercent(
  overlay: Pick<Overlay, 'stroke' | 'strokeWidthPercent'>,
): number {
  if (!normalizeOverlayColor(overlay.stroke)) return 0;
  if (typeof overlay.strokeWidthPercent !== 'number' || !Number.isFinite(overlay.strokeWidthPercent)) {
    return OVERLAY_STROKE_WIDTH_DEFAULT;
  }
  return roundHalfUp(
    clamp(overlay.strokeWidthPercent, OVERLAY_STROKE_WIDTH_MIN, OVERLAY_STROKE_WIDTH_MAX), 2,
  );
}

/** 実効の縁のぼかし (出力幅に対する %)。 */
export function resolveOverlayFeatherPercent(overlay: Pick<Overlay, 'featherPercent'>): number {
  if (typeof overlay.featherPercent !== 'number' || !Number.isFinite(overlay.featherPercent)) {
    return OVERLAY_FEATHER_DEFAULT;
  }
  return roundHalfUp(clamp(overlay.featherPercent, OVERLAY_FEATHER_MIN, OVERLAY_FEATHER_MAX), 2);
}

/** 実効の角丸 (短辺に対する %)。`rounded` の既定は 15。 */
export function resolveOverlayRadiusPercent(
  overlay: Pick<Overlay, 'shape' | 'radiusPercent'>,
): number {
  if (typeof overlay.radiusPercent === 'number' && Number.isFinite(overlay.radiusPercent)) {
    return roundHalfUp(clamp(overlay.radiusPercent, OVERLAY_RADIUS_MIN, OVERLAY_RADIUS_MAX), 1);
  }
  return resolveOverlayShape(overlay) === 'rounded'
    ? OVERLAY_ROUNDED_RADIUS_DEFAULT
    : OVERLAY_RADIUS_DEFAULT;
}

/**
 * 回転 (rotationDeg) を持てる種類。図形 / テキストに加え、斜めに写り込んだ文字を隠せるよう
 * ぼかし / モザイクも回せる (2026-10-29)。それ以外の種類では正規化でキーごと落とす。
 */
export function overlayKindHasRotation(kind: OverlayKind): boolean {
  return kind === 'shape' || kind === 'text' || kind === 'blur' || kind === 'mosaic';
}

/** 実効の回転 (度)。0.5 刻みの half-up。 */
export function resolveOverlayRotationDeg(overlay: Pick<Overlay, 'rotationDeg'>): number {
  if (typeof overlay.rotationDeg !== 'number' || !Number.isFinite(overlay.rotationDeg)) return 0;
  const clamped = clamp(overlay.rotationDeg, OVERLAY_ROTATION_MIN_DEG, OVERLAY_ROTATION_MAX_DEG);
  return roundHalfUp(clamped / OVERLAY_ROTATION_STEP_DEG, 0) * OVERLAY_ROTATION_STEP_DEG;
}

/** 実効の横揃え。 */
export function resolveOverlayTextAlign(overlay: Pick<Overlay, 'align'>): OverlayTextAlign {
  return overlay.align && (OVERLAY_TEXT_ALIGNS as readonly string[]).includes(overlay.align)
    ? overlay.align
    : OVERLAY_TEXT_ALIGN_DEFAULT;
}

/** 文字 1 つの幅係数 (全角 1.0 / 半角 0.55)。ASCII + Latin-1 と半角カナだけを半角と見なす。 */
export function overlayTextCharWidthUnits(codePoint: number): number {
  const half = codePoint <= 0x00ff || (codePoint >= 0xff61 && codePoint <= 0xff9f);
  return half ? OVERLAY_TEXT_HALF_WIDTH_UNITS : OVERLAY_TEXT_FULL_WIDTH_UNITS;
}

/** 1 行の幅係数の和 (フォントサイズ 1px あたりの推定幅)。 */
export function overlayTextLineWidthUnits(line: string): number {
  let total = 0;
  for (const ch of line) total += overlayTextCharWidthUnits(ch.codePointAt(0) ?? 0);
  return roundHalfUp(total, 4);
}

/** 改行で分けた行 (空文字は 1 行として扱う)。 */
export function overlayTextLines(text: string): string[] {
  return text.split('\n');
}

/**
 * rect に収まるフォントサイズ (px)。§4.1-V の式:
 * `min(rect.h_px × 0.82 / 行数, rect.w_px × 0.96 / 最長行の文字幅係数)`。
 * 行送りは `fontSizePx × OVERLAY_TEXT_LINE_HEIGHT` なので、ブロック全体も rect に収まる。
 */
export function overlayTextFontSizePx(
  text: string,
  rectPx: { w: number; h: number },
): number {
  const lines = overlayTextLines(text);
  const lineCount = Math.max(1, lines.length);
  const widest = lines.reduce((max, line) => Math.max(max, overlayTextLineWidthUnits(line)), 0);
  const byHeight = (rectPx.h * OVERLAY_TEXT_HEIGHT_RATIO) / lineCount;
  const byWidth = widest > 0 ? (rectPx.w * OVERLAY_TEXT_WIDTH_RATIO) / widest : byHeight;
  return roundHalfUp(Math.max(1, Math.min(byHeight, byWidth)), 2);
}

/** 出力幅に対する % を px にする (枠線 / 縁のぼかしで共用)。 */
export function overlayPercentOfWidthPx(percent: number, width: number): number {
  return roundHalfUp((width * percent) / 100, 2);
}

/** 角丸の半径 (px)。短辺の半分を超えない。 */
export function overlayRadiusPx(
  overlay: Pick<Overlay, 'shape' | 'radiusPercent'>,
  rectPx: { w: number; h: number },
): number {
  const shortest = Math.min(rectPx.w, rectPx.h);
  const radius = (shortest * resolveOverlayRadiusPercent(overlay)) / 100;
  return roundHalfUp(Math.min(radius, shortest / 2), 2);
}

/**
 * `arrow` の多角形 (rect のローカル座標。左上が原点)。
 * 左中央 → 右中央へ向く矢印で、軸の太さ = 高さ × 0.35、矢じりは高さいっぱい。
 * SVG の polygon / PIL の polygon にそのまま渡せる 7 点。
 */
export function overlayArrowPoints(w: number, h: number): [number, number][] {
  const head = Math.min(h * OVERLAY_ARROW_HEAD_RATIO, w);
  const shaftTop = (h * (1 - OVERLAY_ARROW_SHAFT_RATIO)) / 2;
  const shaftBottom = h - shaftTop;
  const x = w - head;
  const r = (value: number): number => roundHalfUp(value, 2);
  return [
    [0, r(shaftTop)],
    [r(x), r(shaftTop)],
    [r(x), 0],
    [r(w), r(h / 2)],
    [r(x), r(h)],
    [r(x), r(shaftBottom)],
    [0, r(shaftBottom)],
  ];
}

// ── 動画 PIP / 進行バー / カウントダウン (§4.1-V。3 実装で同値) ──────────

/** 実効の「素材の何秒から」 (負値・非数は 0)。 */
export function resolveOverlaySourceStartSec(overlay: Pick<Overlay, 'sourceStartSec'>): number {
  if (typeof overlay.sourceStartSec !== 'number' || !Number.isFinite(overlay.sourceStartSec)) {
    return OVERLAY_VIDEO_SOURCE_START_DEFAULT;
  }
  return roundHalfUp(Math.max(0, overlay.sourceStartSec), 2);
}

/** 実効の「音の扱い」 (`mix` 以外はすべて `mute`)。 */
export function resolveOverlayVideoAudio(overlay: Pick<Overlay, 'audio'>): OverlayVideoAudio {
  return overlay.audio === 'mix' ? 'mix' : OVERLAY_VIDEO_AUDIO_DEFAULT;
}

/** 実効の「末尾フレームで静止するか」 (真偽値の true のときだけ true)。 */
export function resolveOverlayVideoHoldLastFrame(overlay: Pick<Overlay, 'holdLastFrame'>): boolean {
  return overlay.holdLastFrame === true;
}

/**
 * 末尾静止 (`holdLastFrame`) の分岐点 = 「素材を流すフレーム数」。
 *
 * 素材の残り (`srcDurationSec − sourceStartSec`) が表示区間 (`overlaySpanRange`) より短いときだけ
 * 1 以上の整数を返し、レンダラーはそのフレーム数だけ素材を流してから末尾フレームで静止する。
 * `holdLastFrame` でない / 素材尺が不明 (`srcDurationSec` 無し) / 素材が区間を満たす / 残りが
 * 1 フレーム未満 のときは `undefined` (= 従来どおり素材をそのまま流す)。
 * 区間は fps で丸めて比較する (0.5 フレーム未満の差では分岐しない)。
 */
export function overlayVideoHoldSplitFrames(
  overlay: Pick<Overlay, 'startSec' | 'endSec' | 'sourceStartSec' | 'srcDurationSec' | 'holdLastFrame'>,
  durationSec: number,
  fps: number,
): number | undefined {
  if (!resolveOverlayVideoHoldLastFrame(overlay)) return undefined;
  if (typeof overlay.srcDurationSec !== 'number' || !Number.isFinite(overlay.srcDurationSec) || !(fps > 0)) {
    return undefined;
  }
  const { startSec, endSec } = overlaySpanRange(overlay, durationSec);
  const spanFrames = Math.round(Math.max(0, endSec - startSec) * fps);
  const remainingSec = overlay.srcDurationSec - resolveOverlaySourceStartSec(overlay);
  const playFrames = Math.floor(remainingSec * fps + 1e-6);
  if (playFrames < 1 || playFrames >= spanFrames) return undefined;
  return playFrames;
}

/** 進行バーの「進んだ部分」の色 (図形と同じ `fill` キーを使う)。 */
export function resolveOverlayProgressFill(overlay: Pick<Overlay, 'fill'>): string {
  return normalizeOverlayColor(overlay.fill) ?? OVERLAY_PROGRESS_FILL_DEFAULT;
}

/** 進行バーの下地 (トラック) の色。 */
export function resolveOverlayProgressTrack(overlay: Pick<Overlay, 'track'>): string {
  return normalizeOverlayColor(overlay.track) ?? OVERLAY_PROGRESS_TRACK_DEFAULT;
}

/** 進行バーの向き。 */
export function resolveOverlayProgressDirection(
  overlay: Pick<Overlay, 'direction'>,
): OverlayProgressDirection {
  return overlay.direction && (OVERLAY_PROGRESS_DIRECTIONS as readonly string[]).includes(overlay.direction)
    ? overlay.direction
    : OVERLAY_PROGRESS_DIRECTION_DEFAULT;
}

/** カウントダウンの書き方。 */
export function resolveOverlayCountdownFormat(
  overlay: Pick<Overlay, 'format'>,
): OverlayCountdownFormat {
  return overlay.format && (OVERLAY_COUNTDOWN_FORMATS as readonly string[]).includes(overlay.format)
    ? overlay.format
    : OVERLAY_COUNTDOWN_FORMAT_DEFAULT;
}

/**
 * そのオーバーレイ自身の表示区間 (秒)。区間を省略していれば置き場所の全区間 (0〜durationSec)。
 * 進行バーの進み・カウントダウンの残り・キーフレームの基準はすべてこの区間。
 */
export function overlaySpanRange(
  overlay: Pick<Overlay, 'startSec' | 'endSec'>,
  durationSec: number,
): { startSec: number; endSec: number } {
  const startSec = typeof overlay.startSec === 'number' && Number.isFinite(overlay.startSec)
    ? overlay.startSec
    : 0;
  const endSec = typeof overlay.endSec === 'number' && Number.isFinite(overlay.endSec)
    ? overlay.endSec
    : durationSec;
  return { startSec, endSec };
}

/** 進行 p (0〜1)。区間が成立しないときは 0。 */
export function overlayProgressAt(
  overlay: Pick<Overlay, 'startSec' | 'endSec'>,
  sec: number,
  durationSec: number,
): number {
  const { startSec, endSec } = overlaySpanRange(overlay, durationSec);
  const span = endSec - startSec;
  if (!(span > 0)) return 0;
  return roundHalfUp(clamp((sec - startSec) / span, 0, 1), 4);
}

/** 残り秒 (区間の終端 − t)。負にはならない。 */
export function overlayRemainingSec(
  overlay: Pick<Overlay, 'startSec' | 'endSec'>,
  sec: number,
  durationSec: number,
): number {
  const { endSec } = overlaySpanRange(overlay, durationSec);
  return roundHalfUp(Math.max(0, endSec - sec), 3);
}

/** カウントダウンに描く文字 (`seconds` = ceil(残り) / `mmss` = m:ss)。 */
export function overlayCountdownText(
  overlay: Pick<Overlay, 'startSec' | 'endSec' | 'format'>,
  sec: number,
  durationSec: number,
): string {
  const total = Math.ceil(overlayRemainingSec(overlay, sec, durationSec));
  if (resolveOverlayCountdownFormat(overlay) === 'mmss') {
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
  return String(total);
}

/**
 * 進行バーの「塗る部分」(rect の中の %)。3 実装とも同じ矩形を描く
 * (CSS は rect の中の絶対配置、PIL は rect の中の矩形)。
 */
export function overlayProgressFillBox(
  progress: number,
  direction: OverlayProgressDirection,
): { left: number; top: number; width: number; height: number } {
  const p = clamp(progress, 0, 1) * 100;
  const r = (value: number): number => roundHalfUp(value, 2);
  switch (direction) {
    case 'rtl':
      return { left: r(100 - p), top: 0, width: r(p), height: 100 };
    case 'ttb':
      return { left: 0, top: 0, width: 100, height: r(p) };
    case 'btt':
      return { left: 0, top: r(100 - p), width: 100, height: r(p) };
    case 'ltr':
    default:
      return { left: 0, top: 0, width: r(p), height: 100 };
  }
}

// ── キーフレーム (全種類共通。§4.1-V) ───────────────────────────────────

/** そのオーバーレイが「動き」(キーフレーム) を持つか (2 点以上でだけ効く)。 */
export function hasOverlayKeyframes(overlay: Pick<Overlay, 'keyframes'>): boolean {
  return Array.isArray(overlay.keyframes) && overlay.keyframes.length >= 2;
}

/**
 * キーフレームを保存形へ正規化する。
 *
 * - `atSec` は 0 以上・小数第 2 位まで (負の点・数値でない点は落とす)
 * - `rect` は `normalizeOverlayRect` (壊れていればその成分だけ落とす)、`opacity` は 0.1〜1.0
 * - `rect` も `opacity` も無い点は落とす
 * - `atSec` の昇順に並べ替え、**同じ秒は後勝ち**
 * - `maxSec` (区間の長さ) が分かるときは、その外の点を落とす
 * - 残りが 1 点以下なら**キーごと落とす** (1 点では補間できない = 動かないのと同じ)
 */
export function normalizeOverlayKeyframes(
  value: unknown,
  maxSec?: number,
): OverlayKeyframe[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const points: OverlayKeyframe[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    const atRaw = entry.atSec;
    if (typeof atRaw !== 'number' || !Number.isFinite(atRaw) || atRaw < 0) continue;
    const atSec = roundHalfUp(atRaw, OVERLAY_KEYFRAME_SEC_DIGITS);
    if (maxSec !== undefined && atSec > maxSec + 1e-9) continue;
    const next: OverlayKeyframe = { atSec };
    const rect = normalizeOverlayRect(entry.rect);
    if (rect) next.rect = rect;
    if (typeof entry.opacity === 'number' && Number.isFinite(entry.opacity)) {
      next.opacity = roundHalfUp(
        clamp(entry.opacity, OVERLAY_OPACITY_MIN, OVERLAY_OPACITY_MAX), 2,
      );
    }
    if (next.rect === undefined && next.opacity === undefined) continue;
    points.push(next);
  }
  // 昇順 (同じ秒は後勝ち = 後から来た点で上書きする)
  points.sort((a, b) => a.atSec - b.atSec);
  const deduped: OverlayKeyframe[] = [];
  for (const point of points) {
    const last = deduped[deduped.length - 1];
    if (last && Math.abs(last.atSec - point.atSec) < 1e-9) deduped[deduped.length - 1] = point;
    else deduped.push(point);
  }
  const capped = deduped.slice(0, OVERLAY_MAX_KEYFRAMES);
  return capped.length >= 2 ? capped : undefined;
}

/** 2 点の線形補間 (t は 0〜1)。 */
function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/**
 * キーフレーム列から 1 つの成分 (rect / opacity) を取り出して補間する共通処理。
 * **その成分を持つ点だけ**を見るので、rect だけの点と opacity だけの点が混ざっていても
 * 互いに影響しない (§4.1-V の「独立に補間」)。最初の点より前 / 最後の点より後は端の値。
 */
function sampleOverlayKeyframe<V>(
  keyframes: readonly OverlayKeyframe[] | undefined,
  atSec: number,
  read: (point: OverlayKeyframe) => V | undefined,
  mix: (from: V, to: V, t: number) => V,
): V | undefined {
  const points: { atSec: number; value: V }[] = [];
  for (const point of keyframes ?? []) {
    const value = read(point);
    if (value !== undefined) points.push({ atSec: point.atSec, value });
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return undefined;
  if (atSec <= first.atSec) return first.value;
  if (atSec >= last.atSec) return last.value;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (!prev || !next || atSec > next.atSec) continue;
    const span = next.atSec - prev.atSec;
    return mix(prev.value, next.value, span > 0 ? (atSec - prev.atSec) / span : 0);
  }
  return last.value;
}

/** `atSec` の位置での rect (rect を持つ点だけを見る。1 点も無ければ undefined)。 */
export function overlayKeyframeRectAt(
  keyframes: readonly OverlayKeyframe[] | undefined,
  atSec: number,
): OverlayRect | undefined {
  const value = sampleOverlayKeyframe(
    keyframes,
    atSec,
    (point) => point.rect,
    (from, to, t) => ({
      x: lerp(from.x, to.x, t),
      y: lerp(from.y, to.y, t),
      w: lerp(from.w, to.w, t),
      h: lerp(from.h, to.h, t),
    }),
  );
  if (!value) return undefined;
  const r = (raw: number): number => roundHalfUp(raw, OVERLAY_KEYFRAME_RECT_DIGITS);
  return { x: r(value.x), y: r(value.y), w: r(value.w), h: r(value.h) };
}

/** `atSec` の位置での不透明度 (opacity を持つ点だけを見る。1 点も無ければ undefined)。 */
export function overlayKeyframeOpacityAt(
  keyframes: readonly OverlayKeyframe[] | undefined,
  atSec: number,
): number | undefined {
  const value = sampleOverlayKeyframe(
    keyframes, atSec, (point) => point.opacity, (from, to, t) => lerp(from, to, t),
  );
  return value === undefined ? undefined : roundHalfUp(value, OVERLAY_KEYFRAME_OPACITY_DIGITS);
}

/**
 * その時刻に**実際に描く** rect と不透明度 (§4.1-V)。キーフレームが無ければ保存形そのまま。
 * `sec` の基準は置き場所 (動画全体の絶対秒 / シーンの表示秒) で、キーフレームの `atSec` は
 * オーバーレイ自身の表示開始からの秒なので、ここで差を取る。
 */
export function resolveOverlayStateAt(
  overlay: Overlay,
  sec: number,
  durationSec: number,
): { rect: OverlayRect; opacity: number } {
  const base = { rect: overlay.rect, opacity: resolveOverlayOpacity(overlay) };
  if (!hasOverlayKeyframes(overlay)) return base;
  const { startSec } = overlaySpanRange(overlay, durationSec);
  const localSec = sec - startSec;
  return {
    rect: overlayKeyframeRectAt(overlay.keyframes, localSec) ?? base.rect,
    opacity: overlayKeyframeOpacityAt(overlay.keyframes, localSec) ?? base.opacity,
  };
}

/**
 * キーフレームを `[fromSec, toSec]` の窓へ切り出し、先頭を 0 に平行移動する
 * (トリム = `[0, 新しい長さ]`・分割の後半 = `[分割点, ...]`)。
 *
 * 窓の外の点は落とすが、**窓の境界で動きが途切れないよう境界の値を端点として補う**
 * (motion の window と同じ考え方。分割の前後を同じタイムライン時刻でサンプリングすると
 * 同じ rect / opacity になる)。残りが 1 点以下なら undefined (= キーごと落とす)。
 */
export function sliceOverlayKeyframes(
  keyframes: readonly OverlayKeyframe[] | undefined,
  fromSec: number,
  toSec: number,
): OverlayKeyframe[] | undefined {
  if (!keyframes || keyframes.length < 2) return undefined;
  const shift = (point: OverlayKeyframe): OverlayKeyframe => ({
    ...point,
    atSec: roundHalfUp(point.atSec - fromSec, OVERLAY_KEYFRAME_SEC_DIGITS),
  });
  const inside = keyframes.filter(
    (point) => point.atSec >= fromSec - 1e-9 && point.atSec <= toSec + 1e-9,
  );
  const next: OverlayKeyframe[] = inside.map(shift);
  const hasRect = keyframes.some((point) => point.rect !== undefined);
  const hasOpacity = keyframes.some((point) => point.opacity !== undefined);
  const boundary = (atSec: number): OverlayKeyframe => {
    const point: OverlayKeyframe = { atSec: roundHalfUp(atSec - fromSec, OVERLAY_KEYFRAME_SEC_DIGITS) };
    if (hasRect) point.rect = overlayKeyframeRectAt(keyframes, atSec);
    if (hasOpacity) point.opacity = overlayKeyframeOpacityAt(keyframes, atSec);
    return point;
  };
  if (keyframes.some((point) => point.atSec < fromSec - 1e-9)
    && !next.some((point) => Math.abs(point.atSec) < 1e-9)) {
    next.unshift(boundary(fromSec));
  }
  if (Number.isFinite(toSec) && keyframes.some((point) => point.atSec > toSec + 1e-9)
    && !next.some((point) => Math.abs(point.atSec - (toSec - fromSec)) < 1e-9)) {
    next.push(boundary(toSec));
  }
  return next.length >= 2 ? next : undefined;
}

/** 速度変更で表示尺が変わったときのキーフレームの伸縮 (区間と同じ比)。 */
export function scaleOverlayKeyframes(
  keyframes: readonly OverlayKeyframe[] | undefined,
  ratio: number,
): OverlayKeyframe[] | undefined {
  if (!keyframes || keyframes.length < 2) return undefined;
  return keyframes.map((point) => ({
    ...point,
    atSec: roundHalfUp(point.atSec * ratio, OVERLAY_KEYFRAME_SEC_DIGITS),
  }));
}

/** 前後から落とす空白の文字クラス (JS の trim と Python の strip の差を埋めるため明示する)。 */
const OVERLAY_TEXT_TRIM_PATTERN = /^[\s\uFEFF]+|[\s\uFEFF]+$/g;

/**
 * テキストオーバーレイの文言 (1〜80 文字・改行可)。空 / 空白だけの入力は undefined。
 * 改行コードを `\n` に揃え、前後の空白を落としてから 80 **コードポイント**で切る。
 *
 * **JS の `trim()` と Python の `strip()` は落とす文字が違う** (JS は U+FEFF を落とすが Python は
 * 落とさない / Python は U+001C〜U+001F・U+0085 を落とすが JS は落とさない) ので、両実装とも
 * `[\s\uFEFF]` の明示クラスで揃える (残る差は制御文字だけで、実データには現れない)。
 * 切り詰めは `Array.from` で**コードポイント単位** (Python の `text[:80]` と同値。
 * UTF-16 のサロゲートペアの途中では切らない)。
 */
export function normalizeOverlayText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.replace(/\r\n?/g, '\n').replace(OVERLAY_TEXT_TRIM_PATTERN, '');
  if (!text) return undefined;
  return Array.from(text).slice(0, OVERLAY_TEXT_MAX_CHARS).join('');
}

/**
 * テキストオーバーレイの design を保存形へ正規化する。
 * **既知のキーだけを写す** (未知のキーは落とす。テロップ design と違い拡張属性は持たない)。
 */
export function normalizeOverlayTextDesign(value: unknown): OverlayTextDesign | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  for (const key of OVERLAY_TEXT_DESIGN_KEYS) {
    const v = raw[key];
    if (v === undefined || v === null) continue;
    if (key === 'fontWeight') {
      if (v === 'normal' || v === 'bold') next[key] = v;
      else if (typeof v === 'number' && Number.isFinite(v)) next[key] = roundHalfUp(clamp(v, 100, 900), 0);
      continue;
    }
    if (key === 'outlineJoin') {
      if (v === 'round' || v === 'miter') next[key] = v;
      continue;
    }
    if (key === 'color' || key === 'outlineColor' || key === 'outline2Color' || key === 'shadowColor') {
      const color = normalizeOverlayColor(v);
      if (color) next[key] = color;
      continue;
    }
    if (key === 'fontFamily') {
      if (typeof v === 'string' && v.trim()) next[key] = v.trim();
      continue;
    }
    if (typeof v === 'number' && Number.isFinite(v)) {
      // 太さ・ぼかし・広がりは負にしない。不透明度は 0〜1
      const bounded = key === 'shadowOpacity'
        ? clamp(v, 0, 1)
        : key === 'shadowOffsetXPx' || key === 'shadowOffsetYPx'
          ? v
          : Math.max(0, v);
      next[key] = roundHalfUp(bounded, 2);
    }
  }
  return Object.keys(next).length > 0 ? (next as OverlayTextDesign) : undefined;
}

/** `motionOverlay` (§10.13 契約 D) を読む。sceneId と fit が揃っていなければ undefined。 */
function normalizeOverlayMotionMarker(value: unknown): Overlay['motionOverlay'] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as { sceneId?: unknown; fit?: unknown };
  if (typeof raw.sceneId !== 'string' || !raw.sceneId) return undefined;
  if (typeof raw.fit !== 'string' || !(OVERLAY_MOTION_FITS as readonly string[]).includes(raw.fit)) return undefined;
  return { sceneId: raw.sceneId, fit: raw.fit as OverlayMotionFit };
}

/**
 * 外部入力 (AI の提案・古い保存データ) の 1 件を保存形へ正規化する。
 * 落とすのは「kind が不明」「rect が壊れている」「id が無い」に加え、
 * **kind ごとの必須キーが欠けているもの**
 * (`image` / `video` = materialId / `shape` = shape / `text` = text)。
 */
export function normalizeOverlay(value: unknown, fallbackId?: string): Overlay | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const kind = raw.kind;
  if (typeof kind !== 'string' || !(OVERLAY_KINDS as readonly string[]).includes(kind)) return undefined;
  const rect = normalizeOverlayRect(raw.rect);
  if (!rect) return undefined;
  const id =
    typeof raw.id === 'string' && raw.id.length > 0
      ? raw.id
      : typeof fallbackId === 'string' && fallbackId.length > 0
        ? fallbackId
        : undefined;
  if (!id) return undefined;

  const next: Overlay = { id, kind: kind as OverlayKind, rect };

  if (kind === 'image' || kind === 'video') {
    const materialId = raw.materialId;
    if (typeof materialId !== 'string' || materialId.length === 0) return undefined;
    next.materialId = materialId;
    next.isTemporary = raw.isTemporary !== false;
    if (kind === 'video') {
      // 素材の入り点。既定 (0 = 先頭) はキーごと落とす
      const sourceStartSec = resolveOverlaySourceStartSec(raw as Pick<Overlay, 'sourceStartSec'>);
      if (sourceStartSec > 0) next.sourceStartSec = sourceStartSec;
      // 音の扱い / 末尾静止 (§10.13)。既定 (mute / false) と未知の値はキーごと落とす
      const audio = resolveOverlayVideoAudio(raw as Pick<Overlay, 'audio'>);
      if (audio !== OVERLAY_VIDEO_AUDIO_DEFAULT) next.audio = audio;
      if (resolveOverlayVideoHoldLastFrame(raw as Pick<Overlay, 'holdLastFrame'>)) {
        next.holdLastFrame = true;
      }
      // モーション上書きの目印 (§10.13 契約 D)。形が揃っているときだけ素通しで保持する
      const motionOverlay = normalizeOverlayMotionMarker(raw.motionOverlay);
      if (motionOverlay) next.motionOverlay = motionOverlay;
    }
  } else if (kind === 'shape') {
    // 図形は「何を描くか」が必須 (§4.1-V)。壊れた指定は落とす
    const shape = raw.shape;
    if (typeof shape !== 'string' || !(OVERLAY_SHAPES as readonly string[]).includes(shape)) {
      return undefined;
    }
    next.shape = shape as OverlayShape;
    // 保存値 = 実効値: 塗りは必ず埋め、既定値のキー (枠線 0 / ぼかし 0 / 角丸既定) は落とす
    next.fill = resolveOverlayFill(raw as Pick<Overlay, 'fill'>);
    const stroke = normalizeOverlayColor(raw.stroke);
    const strokeWidth = resolveOverlayStrokeWidthPercent({
      stroke, strokeWidthPercent: raw.strokeWidthPercent as number,
    });
    if (stroke && strokeWidth > 0) {
      next.stroke = stroke;
      next.strokeWidthPercent = strokeWidth;
    }
    const feather = resolveOverlayFeatherPercent(raw as Pick<Overlay, 'featherPercent'>);
    if (feather !== OVERLAY_FEATHER_DEFAULT) next.featherPercent = feather;
    const radius = resolveOverlayRadiusPercent({
      shape: shape as OverlayShape, radiusPercent: raw.radiusPercent as number,
    });
    if (radius !== resolveOverlayRadiusPercent({ shape: shape as OverlayShape })) {
      next.radiusPercent = radius;
    }
  } else if (kind === 'text') {
    // テキストは文言が必須 (§4.1-V)
    const text = normalizeOverlayText(raw.text);
    if (text === undefined) return undefined;
    next.text = text;
    const design = normalizeOverlayTextDesign(raw.design);
    if (design) next.design = design;
    const align = resolveOverlayTextAlign(raw as Pick<Overlay, 'align'>);
    if (align !== OVERLAY_TEXT_ALIGN_DEFAULT) next.align = align;
  } else if (kind === 'progress') {
    // 進行バーは色が見た目の正体なので、塗りと下地は必ず埋める (保存値 = 実効値)
    next.fill = resolveOverlayProgressFill(raw as Pick<Overlay, 'fill'>);
    next.track = resolveOverlayProgressTrack(raw as Pick<Overlay, 'track'>);
    const direction = resolveOverlayProgressDirection(raw as Pick<Overlay, 'direction'>);
    if (direction !== OVERLAY_PROGRESS_DIRECTION_DEFAULT) next.direction = direction;
  } else if (kind === 'countdown') {
    // 残り秒はテキストと同じ描画 (design / align)。文言は描画時に作るので持たない
    const design = normalizeOverlayTextDesign(raw.design);
    if (design) next.design = design;
    const align = resolveOverlayTextAlign(raw as Pick<Overlay, 'align'>);
    if (align !== OVERLAY_TEXT_ALIGN_DEFAULT) next.align = align;
    const format = resolveOverlayCountdownFormat(raw as Pick<Overlay, 'format'>);
    if (format !== OVERLAY_COUNTDOWN_FORMAT_DEFAULT) next.format = format;
  } else {
    // blur / mosaic は strength を必ず埋める (保存値 = 実効値)
    next.strength = resolveOverlayStrength({ kind: kind as OverlayKind, strength: raw.strength as number });
  }

  if (kind !== 'blur' && kind !== 'mosaic') {
    // 不透明度は「中身を描く」種類すべてに効く (blur / mosaic は背景を加工するので持たない)
    const opacity = resolveOverlayOpacity(raw as Pick<Overlay, 'opacity'>);
    // 既定値 (1.0) はキーごと落として保存形を汚さない
    if (opacity < OVERLAY_OPACITY_DEFAULT) next.opacity = opacity;
  }
  if (kind === 'image' || kind === 'video') {
    const fit = resolveOverlayFit(raw as Pick<Overlay, 'fit'>);
    if (fit !== OVERLAY_FIT_DEFAULT) next.fit = fit;
  }
  if (overlayKindHasRotation(kind as OverlayKind)) {
    const rotationDeg = resolveOverlayRotationDeg(raw as Pick<Overlay, 'rotationDeg'>);
    if (rotationDeg !== 0) next.rotationDeg = rotationDeg;
  }
  if (kind === 'image' || kind === 'video' || kind === 'progress') {
    // 角丸は図形以外にも効く (§4.11)。既定 (0) はキーごと落とす
    const radius = resolveOverlayRadiusPercent({ radiusPercent: raw.radiusPercent as number });
    if (radius !== OVERLAY_RADIUS_DEFAULT) next.radiusPercent = radius;
  }

  const startSec = typeof raw.startSec === 'number' && Number.isFinite(raw.startSec) && raw.startSec >= 0
    ? roundHalfUp(raw.startSec, 2)
    : undefined;
  const endSec = typeof raw.endSec === 'number' && Number.isFinite(raw.endSec) && raw.endSec >= 0
    ? roundHalfUp(raw.endSec, 2)
    : undefined;
  // end <= start は区間として成立しないので、両方落として「全区間」に倒す
  if (startSec !== undefined && endSec !== undefined && endSec <= startSec) {
    // 区間なし
  } else {
    if (startSec !== undefined && startSec > 0) next.startSec = startSec;
    if (endSec !== undefined) next.endSec = endSec;
  }

  // キーフレーム (§4.1-V)。区間が分かっているときはその外の点を落とす
  // (区間なし = 置き場所の全区間なので、ここでは長さが分からない = 落とせない)。
  const spanSec = next.endSec !== undefined ? next.endSec - (next.startSec ?? 0) : undefined;
  const keyframes = normalizeOverlayKeyframes(raw.keyframes, spanSec);
  if (keyframes) next.keyframes = keyframes;

  if (typeof raw.label === 'string') {
    const label = raw.label.trim().slice(0, OVERLAY_LABEL_MAX_CHARS);
    if (label) next.label = label;
  }
  // 焼き込み文字の目印 (§4.1-U)。AI が ops で書いてきても素通しで保持する
  // (この id で 2 枚を束ねて置き換え / 解除するだけなので、値の意味は検証しない)。
  if (typeof raw.burnedTextId === 'string') {
    const burnedTextId = raw.burnedTextId.trim().slice(0, OVERLAY_BURNED_TEXT_ID_MAX_CHARS);
    if (burnedTextId) next.burnedTextId = burnedTextId;
  }
  return next;
}

/**
 * 読み込み時に使えないオーバーレイを落とす (zod の前処理)。
 *
 * 落ちるのは「種類が不明」「rect が壊れている」「kind ごとの必須キー (image / video = materialId /
 * shape = shape / text = text) が無い」もの。
 * **スキーマで throw させない**のが要点で、1 件の壊れたオーバーレイで
 * プロジェクトごと開けなく (= MP4 も出せなく) するのは割に合わない (§0-2 の「落として注記」。
 * `dropUnusableSeCues` と同じ方針)。保存時は BE の `validate_edit_project_json` が 400 で弾く。
 *
 * **正規化はしない** (署名 URL の導出値 `src` をここで落とさないため。有効だった要素は素通し)。
 */
export function dropUnusableOverlayEntries(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  // **fallbackId を渡さない**: この前処理は要素を書き換えず素通しするので、`id` の無い要素を
  // 残すと直後の `z.array(OverlaySchema)` が `id` 必須で throw する (落とす目的が果たせない)。
  const kept = value.filter((entry) => normalizeOverlay(entry) !== undefined);
  if (kept.length !== value.length) {
    console.warn(
      `[videoEditor] 使えないオーバーレイ ${value.length - kept.length} 件を読み込み時に除外しました`,
    );
  }
  return kept;
}

/** プロジェクト / シーンに載る配列のスキーマ (壊れた要素は前処理で落とす)。 */
export const OverlayArraySchema = z.preprocess(dropUnusableOverlayEntries, z.array(OverlaySchema));

/**
 * `id` を持たない入力 (AI の提案・手書きの JSON) に振る既定の id。
 * backend の `normalize_overlays` が `uuid4()` を振るのと同じ意味で、
 * **`makeId` を省略しても id 無しを理由に落とさない**ようにするためのもの。
 * `crypto.randomUUID` が無い実行環境 (古い jsdom 等) では乱数の短い id に落とす。
 */
function defaultOverlayId(): string {
  const uuid = (globalThis.crypto as Crypto | undefined)?.randomUUID;
  return typeof uuid === 'function'
    ? uuid.call(globalThis.crypto)
    : `ov-${Math.random().toString(36).slice(2, 10)}`;
}

/** 配列の正規化。落ちた要素は dropped に理由付きで積む。 */
export function normalizeOverlays(
  value: unknown,
  makeId?: (index: number) => string,
): { overlays: Overlay[]; dropped: number } {
  if (!Array.isArray(value)) return { overlays: [], dropped: 0 };
  const overlays: Overlay[] = [];
  let dropped = 0;
  value.forEach((raw, index) => {
    const normalized = normalizeOverlay(raw, makeId ? makeId(index) : defaultOverlayId());
    if (normalized) overlays.push(normalized);
    else dropped += 1;
  });
  return { overlays, dropped };
}

/** rect (%) を出力の px に直す (小数第 2 位まで。3 実装で同値)。 */
export function overlayRectPx(
  rect: OverlayRect,
  width: number,
  height: number,
): { x: number; y: number; w: number; h: number } {
  return {
    x: roundHalfUp((width * rect.x) / 100, 2),
    y: roundHalfUp((height * rect.y) / 100, 2),
    w: roundHalfUp((width * rect.w) / 100, 2),
    h: roundHalfUp((height * rect.h) / 100, 2),
  };
}

/** ぼかしの半径 (px)。出力幅に対する % を px に直す。 */
export function overlayBlurRadiusPx(strength: number, width: number): number {
  return roundHalfUp((width * strength) / 100, 2);
}

/** モザイクのセルの一辺 (px)。 */
export function overlayMosaicCellPx(strength: number, width: number): number {
  return roundHalfUp((width * strength) / 100, 2);
}

/**
 * 実際に CSS / PIL へ渡すぼかし半径 (px)。mosaic は「セルの一辺 × OVERLAY_MOSAIC_BLUR_RATIO」の
 * ぼかしで近似する (§4.1-G)。
 */
export function overlayEffectBlurPx(
  overlay: Pick<Overlay, 'kind' | 'strength'>,
  width: number,
): number {
  const strength = resolveOverlayStrength(overlay);
  if (overlay.kind === 'mosaic') {
    return roundHalfUp(overlayMosaicCellPx(strength, width) * OVERLAY_MOSAIC_BLUR_RATIO, 2);
  }
  return overlayBlurRadiusPx(strength, width);
}

/** その時刻にオーバーレイが効いているか (基準は置き場所で変わる。durationSec = 全区間の終わり)。 */
export function isOverlayActiveAt(overlay: Overlay, sec: number, durationSec: number): boolean {
  return isSpanActiveAt(overlay, sec, durationSec);
}

/** 覆っている面積 (画面に対する %)。 */
export function overlayAreaPercent(overlay: Pick<Overlay, 'rect'>): number {
  return roundHalfUp((overlay.rect.w * overlay.rect.h) / 100, 1);
}

/**
 * AI / UI に見せる 1 行の説明 (「ぼかし x20 y40 30×12% 0.4-2.4s 強さ2.5 (PC 画面の文字)」)。
 *
 * `aliasByMaterial` (`materialId` → `IMG-1` / `VID-1`) を渡すと、画像 / 動画のオーバーレイに
 * 候補の別名を添える
 * (要約を読んだ AI が「どの候補画像か」を同定できるようにするため)。FE / レンダラーには候補一覧が
 * 無いので通常は省略するが、backend の `describe_overlay` と引数を揃えてある。
 * `fps` を渡すと時間を画面向けに書く (位置は "MM:SS:FF"、長さは "S:FF" (秒:フレーム))。
 * 省略時は秒のまま (AI の要約・backend の文言と揃える)。
 */
export function describeOverlay(
  overlay: Overlay,
  aliasByMaterial?: Record<string, string>,
  fps?: number,
): string {
  const at = (sec: number) => (fps === undefined ? `${sec}s` : formatTimecode(sec, fps));
  const parts: string[] = [OVERLAY_KIND_LABELS[overlay.kind]];
  if ((overlay.kind === 'image' || overlay.kind === 'video') && aliasByMaterial) {
    const alias = aliasByMaterial[overlay.materialId ?? ''];
    if (alias) parts.push(alias);
  }
  const { rect } = overlay;
  parts.push(`x${rect.x} y${rect.y} ${rect.w}×${rect.h}%`);
  if (overlay.startSec !== undefined || overlay.endSec !== undefined) {
    const start = overlay.startSec ?? 0;
    if (fps === undefined) {
      parts.push(overlay.endSec !== undefined ? `${start}-${overlay.endSec}s` : `${start}s〜`);
    } else {
      parts.push(`${at(start)}〜${overlay.endSec !== undefined ? at(overlay.endSec) : ''}`);
    }
  } else {
    parts.push('全区間');
  }
  if (overlay.kind === 'image' || overlay.kind === 'video') {
    const opacity = resolveOverlayOpacity(overlay);
    if (opacity < 1) parts.push(`不透明度${Math.round(opacity * 100)}%`);
    if (resolveOverlayFit(overlay) === 'cover') parts.push('枠いっぱい');
    if (overlay.kind === 'video') {
      const sourceStartSec = resolveOverlaySourceStartSec(overlay);
      if (sourceStartSec > 0) parts.push(`素材${at(sourceStartSec)}〜`);
      parts.push(OVERLAY_VIDEO_AUDIO_LABELS[resolveOverlayVideoAudio(overlay)]);
      if (resolveOverlayVideoHoldLastFrame(overlay)) parts.push('末尾で静止');
    }
    const radius = resolveOverlayRadiusPercent({ radiusPercent: overlay.radiusPercent });
    if (radius > 0) parts.push(`半径${radius}%`);
  } else if (overlay.kind === 'shape') {
    parts.push(`${OVERLAY_SHAPE_LABELS[resolveOverlayShape(overlay)]} ${resolveOverlayFill(overlay)}`);
    const strokeWidth = resolveOverlayStrokeWidthPercent(overlay);
    if (strokeWidth > 0) parts.push(`枠${normalizeOverlayColor(overlay.stroke)} ${strokeWidth}%`);
    const feather = resolveOverlayFeatherPercent(overlay);
    if (feather > 0) parts.push(`ぼかし縁${feather}%`);
    const radius = resolveOverlayRadiusPercent(overlay);
    if (radius > 0) parts.push(`半径${radius}%`);
  } else if (overlay.kind === 'text') {
    parts.push(`「${overlay.text ?? ''}」`);
    const color = normalizeOverlayColor(overlay.design?.color);
    if (color) parts.push(color);
    const align = resolveOverlayTextAlign(overlay);
    if (align !== OVERLAY_TEXT_ALIGN_DEFAULT) parts.push(align === 'left' ? '左揃え' : '右揃え');
  } else if (overlay.kind === 'progress') {
    parts.push(OVERLAY_PROGRESS_DIRECTION_LABELS[resolveOverlayProgressDirection(overlay)]);
    parts.push(resolveOverlayProgressFill(overlay));
    const track = resolveOverlayProgressTrack(overlay);
    if (track !== OVERLAY_PROGRESS_TRACK_DEFAULT) parts.push(`下地${track}`);
    const radius = resolveOverlayRadiusPercent({ radiusPercent: overlay.radiusPercent });
    if (radius > 0) parts.push(`半径${radius}%`);
  } else if (overlay.kind === 'countdown') {
    parts.push(OVERLAY_COUNTDOWN_FORMAT_LABELS[resolveOverlayCountdownFormat(overlay)]);
    const color = normalizeOverlayColor(overlay.design?.color);
    if (color) parts.push(color);
    const align = resolveOverlayTextAlign(overlay);
    if (align !== OVERLAY_TEXT_ALIGN_DEFAULT) parts.push(align === 'left' ? '左揃え' : '右揃え');
  } else {
    parts.push(`強さ${resolveOverlayStrength(overlay)}`);
  }
  if (overlay.kind === 'shape' || overlay.kind === 'text') {
    const opacity = resolveOverlayOpacity(overlay);
    if (opacity < 1) parts.push(`不透明度${Math.round(opacity * 100)}%`);
  }
  if (overlayKindHasRotation(overlay.kind)) {
    const rotationDeg = resolveOverlayRotationDeg(overlay);
    if (rotationDeg !== 0) parts.push(`回転${rotationDeg}°`);
  }
  if (overlay.kind === 'progress' || overlay.kind === 'countdown') {
    const opacity = resolveOverlayOpacity(overlay);
    if (opacity < 1) parts.push(`不透明度${Math.round(opacity * 100)}%`);
  }
  // キーフレーム (§4.1-V) は「点数 / 長さ」だけを出す (AI が自分の書いた動きを読み返せるように。
  // 個々の座標は出さない — UI も AI も数値では触らないので、要約に載せても使い道が無い)。
  if (hasOverlayKeyframes(overlay)) {
    const keyframes = overlay.keyframes!;
    const spanSec = roundHalfUp(
      keyframes[keyframes.length - 1]!.atSec - keyframes[0]!.atSec, 2,
    );
    parts.push(`動き${keyframes.length}点/${fps === undefined ? `${spanSec}秒` : formatDurationLabel(spanSec, fps)}`);
  }
  if (overlay.label) parts.push(`(${overlay.label})`);
  return parts.join(' ');
}

/** プリセット位置から rect を作る (幅・高さは呼び出し側が決める)。 */
export function overlayRectForPreset(
  preset: OverlayPositionPreset,
  w: number,
  h: number,
): OverlayRect {
  const m = OVERLAY_PRESET_MARGIN_PERCENT;
  const right = OVERLAY_SIZE_MAX - m - w;
  const bottom = OVERLAY_SIZE_MAX - m - h;
  switch (preset) {
    // 全面は幅 / 高さを無視して画面いっぱい
    case 'full':
      return { ...OVERLAY_FULL_RECT };
    case 'top_left':
      return normalizeOverlayRect({ x: m, y: m, w, h })!;
    case 'top_right':
      return normalizeOverlayRect({ x: right, y: m, w, h })!;
    case 'bottom_left':
      return normalizeOverlayRect({ x: m, y: bottom, w, h })!;
    case 'bottom_right':
      return normalizeOverlayRect({ x: right, y: bottom, w, h })!;
    case 'center':
    default:
      return normalizeOverlayRect({ x: (OVERLAY_SIZE_MAX - w) / 2, y: (OVERLAY_SIZE_MAX - h) / 2, w, h })!;
  }
}

/** rect がどのプリセット位置に一致するか (一致しなければ null = 「カスタム」)。 */
export function overlayPresetOf(rect: OverlayRect): OverlayPositionPreset | null {
  // 全面は幅 / 高さまで一致したときだけ (x=0・y=0 の矩形は「中央」の 100×100 とも一致するので先に判定)
  if (isOverlayFullRect(rect)) return 'full';
  for (const preset of OVERLAY_POSITION_PRESETS) {
    if (preset === 'full') continue;
    const candidate = overlayRectForPreset(preset, rect.w, rect.h);
    if (Math.abs(candidate.x - rect.x) < 1e-6 && Math.abs(candidate.y - rect.y) < 1e-6) return preset;
  }
  return null;
}

/**
 * シーン相対のオーバーレイを、新しい表示尺に合わせて詰める (テロップと同じ規則)。
 * 区間が成立しなくなったもの (start >= end) は落とす。区間を持たないものはそのまま残す。
 *
 * 実体は schema/timedSpan.ts の共通ロジック (映像エフェクト §4.1-S と同じ式・同じ丸め)。
 */
export function clampOverlaysToDuration(overlays: Overlay[], durationSec: number): Overlay[] {
  return clampSpansToDuration(overlays, durationSec, (produced, original) =>
    adjustOverlayKeyframesForSpan(produced, original, durationSec),
  ).kept;
}

/**
 * 区間を詰め直したオーバーレイのキーフレームを、新しい窓 `[0, 新しい長さ]` へ切り出す
 * (§4.1-V)。区間を持たない (= 置き場所の全区間) ものは `durationSec` を長さとして使う。
 */
function adjustOverlayKeyframesForSpan(
  produced: Overlay,
  original: Overlay,
  durationSec: number,
): Overlay {
  if (!hasOverlayKeyframes(original)) return produced;
  const { startSec, endSec } = overlaySpanRange(produced, durationSec);
  const keyframes = sliceOverlayKeyframes(original.keyframes, 0, Math.max(0, endSec - startSec));
  const next: Overlay = { ...produced };
  if (keyframes) next.keyframes = keyframes;
  else delete next.keyframes;
  return next;
}

/**
 * 速度変更でシーンの表示尺が変わったときの詰め直し (テロップの rescaleCaptionsForSpeed と同じ比率)。
 * 区間を持たないオーバーレイは「シーン全体」の意味なので触らない。
 */
export function rescaleOverlaysForSpeed(
  overlays: Overlay[] | undefined,
  prevSpeed: number,
  nextSpeed: number,
): Overlay[] | undefined {
  const rescaled = rescaleSpansForSpeed(overlays, prevSpeed, nextSpeed);
  // 触っていない (速度が変わっていない / 不正) ときは同じ配列が返る = キーフレームも触らない
  if (!rescaled || rescaled === overlays) return rescaled;
  const ratio = prevSpeed / nextSpeed;
  return rescaled.map((overlay) => {
    // キーフレームは**区間を持たないものも含めて**伸縮する (基準はオーバーレイ自身の
    // 表示開始からの秒で、区間なし = シーン全体の長さも速度で変わるため)。
    if (!hasOverlayKeyframes(overlay)) return overlay;
    const keyframes = scaleOverlayKeyframes(overlay.keyframes, ratio);
    return keyframes ? { ...overlay, keyframes } : overlay;
  });
}

/**
 * シーンを表示秒の割合ではなく**表示秒 atSec** で分割したときの、前半 / 後半のオーバーレイ。
 * テロップ (`_op_split_item`) と同じ 3 分岐: 前半だけ / 後半だけ / 境界をまたぐものは切り分ける。
 * 区間を持たないもの (= シーン全体) は両方に複製する (後半は新しい id)。
 *
 * 後半へ移るときは、経過したぶん (`atSec − 自分の表示開始`) だけ
 * **キーフレームの atSec を平行移動**し、**動画 PIP の `sourceStartSec` を進める**
 * (BGM の `splitAudioCue` と同じ規約。進めないと後半で素材が頭に巻き戻る)。
 */
export function splitOverlays(
  overlays: Overlay[] | undefined,
  atSec: number,
  makeId: (index: number) => string,
): { first: Overlay[]; second: Overlay[] } {
  return splitSpans(overlays, atSec, makeId, (produced, original, part) => {
    const originStart = original.startSec ?? 0;
    // 後半へ移るぶんの「自分の表示開始からの経過秒」。キーフレームの平行移動と
    // 動画 PIP の素材の入り点は**同じ値**だけ進む (BGM の split_audio_cue と同じ規約)。
    const shift = part === 'second' ? Math.max(0, atSec - originStart) : 0;
    let next: Overlay = produced;
    // 動画 PIP (§4.1-V) は分割点まで再生が進んでいるので、後半は素材の入り点も進める
    // (足さないと後半で素材が頭へ巻き戻る)。速度変更 (rescale) では入り点を動かさない。
    if (part === 'second' && original.kind === 'video' && shift > 0) {
      const sourceStartSec = roundHalfUp(resolveOverlaySourceStartSec(original) + shift, 2);
      next = { ...next, sourceStartSec };
    }
    if (hasOverlayKeyframes(original)) {
      let keyframes: OverlayKeyframe[] | undefined;
      if (part === 'first') {
        // 前半の窓は [0, 分割点まで]。区間を持たないものは分割点がそのまま終わり
        const end = produced.endSec ?? atSec;
        keyframes = sliceOverlayKeyframes(original.keyframes, 0, Math.max(0, end - originStart));
      } else {
        // 後半は分割点ぶん平行移動する (自分の表示開始より前に分割点があるときだけ動く)
        const producedSpan = produced.endSec !== undefined
          ? produced.endSec - (produced.startSec ?? 0)
          : Infinity;
        keyframes = sliceOverlayKeyframes(original.keyframes, shift, shift + producedSpan);
      }
      next = { ...next };
      if (keyframes) next.keyframes = keyframes;
      else delete next.keyframes;
    }
    return next;
  });
}
